import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogAction, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select';
import { PageLoading } from '@/components/ui/loading';
import { ROLE_LABELS, normalizeRole } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { Plus, Pencil, Search, Trash2, HardHat, ClipboardCheck, Check, FolderKanban } from 'lucide-react';

interface AssignedProject {
  id: string;
  name: string;
  status: string;
}

interface UserRow {
  id: string;
  unique_id?: string;
  email?: string;
  name: string;
  phone?: string | null;
  is_active: number;
  is_temp_password: number;
  project_count?: number;
  roles?: { role_id: string; name: string }[];
}

interface UsersSummary {
  siteEngineers: number;
  projectManagers: number;
}

type SummaryKind = 'site_engineers' | 'project_managers';

function summaryCountKey(kind: SummaryKind): keyof UsersSummary {
  return kind === 'site_engineers' ? 'siteEngineers' : 'projectManagers';
}

/** Reason the current admin may not run lifecycle actions on this user, or null when allowed. */
function manageDisabledReason(u: UserRow, currentUserId?: string): string | null {
  const roles = (u.roles ?? []).map((r) => normalizeRole(r.name));
  if (u.id === currentUserId) return 'You cannot manage your own account';
  if (roles.includes('SUPER_ADMIN')) return 'Super Admin accounts cannot be managed from the organization user list';
  if (roles.includes('ORG_ADMIN')) return 'Organization administrator accounts cannot be managed from the organization user list';
  return null;
}

/** Reason permanent deletion is blocked for this row, or null when allowed. */
function deleteDisabledReason(u: UserRow, currentUserId?: string): string | null {
  const roles = (u.roles ?? []).map((r) => normalizeRole(r.name));
  if (u.id === currentUserId) return 'You cannot delete your own account';
  if (roles.includes('SUPER_ADMIN')) return 'Super Admin accounts cannot be deleted from the organization user list';
  return null;
}

function roleLabel(u: UserRow): string {
  const labels = (u.roles ?? [])
    .map((r) => ROLE_LABELS[normalizeRole(r.name)] ?? r.name);
  return labels.length ? labels.join(', ') : 'None';
}

/** Summarizes a user's project access: admins are always org-wide, everyone else shows their assignment count. */
function projectAccessLabel(u: UserRow): string {
  const roles = (u.roles ?? []).map((r) => normalizeRole(r.name));
  if (roles.includes('ORG_ADMIN') || roles.includes('SUPER_ADMIN')) return 'Full access';
  const n = u.project_count ?? 0;
  return n === 0 ? 'None' : n === 1 ? '1 project' : `${n} projects`;
}

const SUMMARY_CARDS: {
  kind: SummaryKind;
  label: string;
  description: string;
  icon: ReactNode;
  iconClass: string;
  selectedClass: string;
  ariaLabel: string;
}[] = [
  {
    kind: 'site_engineers',
    label: 'Site Engineers',
    description: 'Can create Departmental Labour-Requirement & Departmental Labour -Actual',
    icon: <HardHat size={22} />,
    iconClass: 'bg-brand-500/15 text-brand-600 dark:text-brand-300',
    selectedClass: 'border-brand-500 bg-brand-500/5 ring-2 ring-brand-500/25',
    ariaLabel: 'Filter users by Site Engineers',
  },
  {
    kind: 'project_managers',
    label: 'Project Managers',
    description: 'Can approve at manager stage',
    icon: <ClipboardCheck size={22} />,
    iconClass: 'bg-accent-300/15 text-accent-300',
    selectedClass: 'border-accent-300 bg-accent-300/5 ring-2 ring-accent-300/25',
    ariaLabel: 'Filter users by Project Managers',
  },
];

function SummaryCard({
  label,
  description,
  icon,
  iconClass,
  selectedClass,
  ariaLabel,
  count,
  loading,
  error,
  selected,
  onRetry,
  onToggle,
}: {
  label: string;
  description: string;
  icon: ReactNode;
  iconClass: string;
  selectedClass: string;
  ariaLabel: string;
  count?: number;
  loading: boolean;
  error: boolean;
  selected: boolean;
  onRetry: () => void;
  onToggle: () => void;
}) {
  if (loading) {
    return <Skeleton className="h-[104px] w-full rounded-xl" />;
  }
  if (error) {
    return (
      <div className="flex h-[104px] flex-col items-start justify-center gap-2 rounded-xl border border-default bg-surface p-4 shadow-sm">
        <p className="text-sm text-error">Unable to load summary</p>
        <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={ariaLabel}
      aria-pressed={selected}
      className={cn(
        'group flex h-[104px] w-full items-center gap-4 rounded-xl border bg-surface p-4 text-left shadow-sm transition-colors hover:bg-surface-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500',
        selected ? selectedClass : 'border-default'
      )}
    >
      <span className={cn('flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg', iconClass)}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-secondary">{label}</span>
        <span className="block text-2xl font-bold leading-tight text-primary">{count}</span>
        <span className="block truncate text-xs text-muted">{description}</span>
      </span>
      {selected && <Check size={18} className="flex-shrink-0 text-primary" />}
    </button>
  );
}

export default function UsersListPage() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<SummaryKind | null>(null);
  const [activateTarget, setActivateTarget] = useState<UserRow | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<UserRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [projectsTarget, setProjectsTarget] = useState<UserRow | null>(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [projectsError, setProjectsError] = useState('');
  const { user: currentUser } = useAuth();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', search, roleFilter ?? 'all'],
    queryFn: async () => {
      const query = new URLSearchParams();
      query.set('search', search);
      if (roleFilter) {
        query.set('role', roleFilter);
        // Show every qualifying user (not just the default page) so the
        // filtered table reconciles with the summary card count.
        query.set('limit', '1000');
      }
      const r = await api.get<{ success: boolean; data: UserRow[] }>(`/users?${query.toString()}`);
      return r.data ?? [];
    },
    // Keep the previous rows on screen while the search text or role filter
    // changes so the page (and its summary cards) never unmount to PageLoading.
    placeholderData: (previous) => previous,
  });

  const summaryQuery = useQuery({
    queryKey: ['admin-users-summary'],
    queryFn: async () => {
      const r = await api.get<{ success: boolean; data: UsersSummary }>('/users/summary');
      return r.data;
    },
  });

  const activeProjectsQuery = useQuery({
    queryKey: ['admin-active-projects'],
    enabled: !!projectsTarget,
    queryFn: async () => {
      const r = await api.get<{ success: boolean; data: { id: string; name: string }[] }>('/projects?availability=active&limit=1000');
      return r.data ?? [];
    },
  });

  const userProjectsQuery = useQuery({
    queryKey: ['admin-user-projects', projectsTarget?.id ?? null],
    enabled: !!projectsTarget,
    queryFn: async () => {
      const r = await api.get<{ success: boolean; data: AssignedProject[] }>(`/users/${projectsTarget!.id}/projects`);
      return r.data ?? [];
    },
  });

  // Active projects plus any assigned project that is no longer active (kept so
  // existing assignments stay visible as removable chips in the modal).
  const projectOptions: MultiSelectOption[] = (() => {
    const map = new Map<string, string>();
    for (const p of activeProjectsQuery.data ?? []) map.set(p.id, p.name);
    for (const a of userProjectsQuery.data ?? []) {
      if (!map.has(a.id)) map.set(a.id, a.status === 'active' ? a.name : `${a.name} (${a.status})`);
    }
    return [...map].map(([value, label]) => ({ value, label }));
  })();

  useEffect(() => {
    if (projectsTarget) {
      setSelectedProjectIds((userProjectsQuery.data ?? []).map((p) => p.id));
    }
  }, [projectsTarget, userProjectsQuery.data]);

  const invalidateUsers = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    queryClient.invalidateQueries({ queryKey: ['admin-users-summary'] });
  };

  const activateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/users/${id}/activate`),
    onSuccess: () => { addToast('User activated successfully.', 'success'); invalidateUsers(); setActivateTarget(null); },
    onError: (err: any) => { addToast(err.message, 'error'); setActivateTarget(null); },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/users/${id}/deactivate`),
    onSuccess: () => { addToast('User deactivated successfully.', 'success'); invalidateUsers(); setDeactivateTarget(null); },
    onError: (err: any) => { addToast(err.message, 'error'); setDeactivateTarget(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}?permanent=true`),
    onSuccess: () => {
      addToast('User deleted successfully.', 'success');
      invalidateUsers();
      setDeleteTarget(null); setDeleteError('');
    },
    onError: (err: any) => { addToast(err.message || 'Unable to delete user.', 'error'); setDeleteError(err.message || 'Unable to delete user.'); },
  });

  const saveProjectsMutation = useMutation({
    mutationFn: () => api.put(`/users/${projectsTarget!.id}/projects`, { projectIds: selectedProjectIds }),
    onSuccess: () => {
      addToast('Project assignments saved.', 'success');
      invalidateUsers();
      setProjectsTarget(null); setProjectsError('');
    },
    onError: (err: any) => { const msg = err.message || 'Unable to save project assignments.'; addToast(msg, 'error'); setProjectsError(msg); },
  });

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-heading font-bold text-primary">Users</h1>
        <Link to="/admin/users/new"><Button className="gap-2 min-h-[44px] w-full sm:w-auto"><Plus size={16} />Add User</Button></Link>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {SUMMARY_CARDS.map((card) => (
          <SummaryCard
            key={card.kind}
            {...card}
            count={summaryQuery.data?.[summaryCountKey(card.kind)]}
            loading={summaryQuery.isLoading}
            error={summaryQuery.isError}
            selected={roleFilter === card.kind}
            onRetry={() => summaryQuery.refetch()}
            onToggle={() => setRoleFilter((f) => (f === card.kind ? null : card.kind))}
          />
        ))}
      </div>
      <Card>
        <CardContent className="p-4">
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} /><Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" /></div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <div className="hidden md:block">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Unique ID</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Roles</TableHead><TableHead>Projects</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {data?.map((u: UserRow) => {
                const disabledReason = manageDisabledReason(u, currentUser?.id);
                const canDeleteReason = deleteDisabledReason(u, currentUser?.id);
                return (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.unique_id || '—'}</TableCell>
                    <TableCell>{u.email || '—'}</TableCell>
                    <TableCell>{u.phone || '-'}</TableCell>
                    <TableCell><div className="flex gap-1 flex-wrap">{u.roles?.map((r) => <Badge key={r.role_id} variant="info" className="text-xs">{ROLE_LABELS[normalizeRole(r.name)] ?? r.name}</Badge>)}</div></TableCell>
                    <TableCell>{projectAccessLabel(u)}</TableCell>
                    <TableCell><Badge variant={u.is_active ? 'approved' : 'inactive'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 items-center">
                        <Link to={`/admin/users/${u.id}/edit`}><Button variant="ghost" size="sm" aria-label="Edit user" title="Edit user"><Pencil size={14} /></Button></Link>
                        <Button
                          variant="ghost" size="sm"
                          className="gap-1 text-primary hover:text-primary"
                          disabled={!!disabledReason}
                          title={disabledReason ?? 'Manage project assignments'}
                          aria-label="Manage projects"
                          onClick={() => { setProjectsError(''); setProjectsTarget(u); }}
                        ><FolderKanban size={14} />Projects</Button>
                        {u.is_active ? (
                          <Button
                            variant="ghost" size="sm"
                            className="text-amber-600 hover:text-amber-700"
                            disabled={!!disabledReason}
                            title={disabledReason ?? 'Deactivate user'}
                            aria-label="Deactivate user"
                            onClick={() => setDeactivateTarget(u)}
                          >Deactivate</Button>
                        ) : (
                          <Button
                            variant="ghost" size="sm"
                            className="text-green-600 hover:text-green-700"
                            disabled={!!disabledReason}
                            title={disabledReason ?? 'Activate user'}
                            aria-label="Activate user"
                            onClick={() => setActivateTarget(u)}
                          >Activate</Button>
                        )}
                        <Button
                          variant="ghost" size="sm"
                          className="gap-1 text-red-600 hover:text-red-700"
                          disabled={!!canDeleteReason || deleteMutation.isPending}
                          title={canDeleteReason ?? 'Delete this user'}
                          aria-label="Delete user"
                          onClick={() => { setDeleteError(''); setDeleteTarget(u); }}
                        ><Trash2 size={14} />Delete</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!data || data.length === 0) && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted">No users found</TableCell></TableRow>}
            </TableBody>
          </Table>
          </div>
          <div className="md:hidden space-y-3 p-4">
            {(!data || data.length === 0) ? (
              <p className="text-center py-8 text-muted">No users found</p>
            ) : data.map((u: UserRow) => {
              const disabledReason = manageDisabledReason(u, currentUser?.id);
              const canDeleteReason = deleteDisabledReason(u, currentUser?.id);
              return (
                <div key={u.id} className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
                  <p className="font-medium text-primary break-words">{u.name}</p>
                  <dl className="text-sm space-y-1">
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Unique ID</dt><dd className="text-primary text-right break-words">{u.unique_id || '—'}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Email</dt><dd className="text-primary text-right break-words">{u.email || '—'}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Phone</dt><dd className="text-primary text-right break-words">{u.phone || '-'}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Roles</dt><dd className="text-primary text-right break-words"><div className="flex gap-1 flex-wrap justify-end">{u.roles?.map((r) => <Badge key={r.role_id} variant="info" className="text-xs">{ROLE_LABELS[normalizeRole(r.name)] ?? r.name}</Badge>)}</div></dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Projects</dt><dd className="text-primary text-right">{projectAccessLabel(u)}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Status</dt><dd><Badge variant={u.is_active ? 'approved' : 'inactive'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></dd></div>
                  </dl>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Link to={`/admin/users/${u.id}/edit`}><Button variant="ghost" size="sm" aria-label="Edit user" className="min-h-[44px]">Edit</Button></Link>
                    <Button variant="ghost" size="sm" className="gap-1 text-primary hover:text-primary min-h-[44px]" disabled={!!disabledReason} title={disabledReason ?? 'Manage project assignments'} aria-label="Manage projects" onClick={() => { setProjectsError(''); setProjectsTarget(u); }}>Projects</Button>
                    {u.is_active ? (
                      <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 min-h-[44px]" disabled={!!disabledReason} title={disabledReason ?? 'Deactivate user'} aria-label="Deactivate user" onClick={() => setDeactivateTarget(u)}>Deactivate</Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 min-h-[44px]" disabled={!!disabledReason} title={disabledReason ?? 'Activate user'} aria-label="Activate user" onClick={() => setActivateTarget(u)}>Activate</Button>
                    )}
                    <Button variant="ghost" size="sm" className="gap-1 text-red-600 hover:text-red-700 min-h-[44px]" disabled={!!canDeleteReason || deleteMutation.isPending} title={canDeleteReason ?? 'Delete this user'} aria-label="Delete user" onClick={() => { setDeleteError(''); setDeleteTarget(u); }}>Delete</Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!activateTarget} onOpenChange={(o) => { if (!o) setActivateTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {activateTarget?.name} ({activateTarget?.unique_id || activateTarget?.email}) will be able to sign in again. Their roles are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-success hover:opacity-90" onClick={() => activateTarget && activateMutation.mutate(activateTarget.id)}>Activate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deactivateTarget} onOpenChange={(o) => { if (!o) setDeactivateTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateTarget?.name} ({deactivateTarget?.unique_id || deactivateTarget?.email}) will no longer be able to sign in, but their data and history will be preserved. Their active sessions will be ended.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-warning text-[#081631] hover:opacity-90" onClick={() => deactivateTarget && deactivateMutation.mutate(deactivateTarget.id)}>Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteError(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete user?</DialogTitle>
            <DialogDescription>
              This action is irreversible. The user will be permanently removed along with their roles and sessions. Their Departmental Labour-Requirement and Departmental Labour -Actual records will be preserved and shown as <span className="font-medium text-primary">Deleted User</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-default bg-surface-secondary p-4 text-sm space-y-1">
              <p><span className="text-secondary">Name:</span> <span className="font-semibold">{deleteTarget?.name}</span></p>
              <p><span className="text-secondary">Unique ID:</span> <span className="font-semibold">{deleteTarget?.unique_id || '—'}</span></p>
              <p><span className="text-secondary">Email:</span> <span className="font-semibold">{deleteTarget?.email || '—'}</span></p>
              <p><span className="text-secondary">Role:</span> <span className="font-semibold">{deleteTarget ? roleLabel(deleteTarget) : ''}</span></p>
            </div>
            {deleteError && <p className="text-sm text-error">{deleteError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteError(''); }}>Cancel</Button>
            <Button variant="destructive" disabled={deleteMutation.isPending} loading={deleteMutation.isPending} onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}>Delete User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!projectsTarget} onOpenChange={(o) => { if (!o) { setProjectsTarget(null); setProjectsError(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Projects</DialogTitle>
            <DialogDescription>
              Choose which active projects {projectsTarget?.name} ({projectsTarget?.unique_id || projectsTarget?.email}) can access. Users only see data for the projects they are assigned to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <MultiSelect
              value={selectedProjectIds}
              onValueChange={setSelectedProjectIds}
              options={projectOptions}
              ariaLabel="Projects"
              isLoading={activeProjectsQuery.isLoading || userProjectsQuery.isLoading}
              isError={activeProjectsQuery.isError || userProjectsQuery.isError}
              onRetry={() => { activeProjectsQuery.refetch(); userProjectsQuery.refetch(); }}
            />
            {projectsError && <p className="text-sm text-error">{projectsError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setProjectsTarget(null); setProjectsError(''); }}>Cancel</Button>
            <Button disabled={saveProjectsMutation.isPending} loading={saveProjectsMutation.isPending} onClick={() => saveProjectsMutation.mutate()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

